import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export type MousemoveListener = (event: Pick<MouseEvent, 'clientX' | 'clientY'>) => void
/**
 * An event emitter that can be used to listen to mousemove events.
 */
export interface MousemoveTarget {
  addEventListener: (type: 'mousemove', listener: MousemoveListener) => void
  removeEventListener: (type: 'mousemove', listener: MousemoveListener) => void
}

export interface PeekZoneArgs {
  readonly notchRef: React.RefObject<HTMLDivElement | null>
  /**
   * Allow substituting a different target for mousemove event.
   *
   * This is necessary because Inkwell relies on events forwarded by the host,
   * not true DOM events.
   *
   * Defaults to `document`.
   */
  readonly mousemoveTarget?: MousemoveTarget

  /**
   * optional, support docking the notch to different screen edges, by default this will be 'right'
   */
  readonly clampEdge?: 'top' | 'right' | 'bottom' | 'left'
}

/**
 * if the mouse is within this distance of the notch, the notch will be shown
 */
export const PEEK_ZONE_DISTANCE_THRESHOLD = 400

/**
 * if the mouse is within this distance of a clamped edge, the notch will be shown
 */
export const PEEK_ZONE_CLAMPED_EDGE_THRESHOLD = 10

/**
 * if the mouse is within PEEK_ZONE_CLAMPED_EDGE_THRESHOLD of the clamped edge, simulate
 * the mouse being this distance from the notch to begin peeking
 */
export const PEEK_ZONE_CLAMPED_EDGE_SIMULATED_DISTANCE = 350

/**
 * Hook used to get the current peek zone range for the assistant notch, determine if cursor is in peek zone,
 * and handle updating the notch's state based on the cursor's proximity to peek zone.
 * @param props
 * @param props.notchRef Ref of the peekaboo zone wrapper for assistant notch
 * @returns Details on the current screen width, horizontal peek zone range,
 * vertical peek zone range, if cursor is in peek zone, notch's current state, notch's set state function.
 */
export const useHiddenPercent = ({ notchRef, mousemoveTarget, clampEdge }: PeekZoneArgs) => {
  const notchPositionRef = useRef<DOMRect>()
  const animationFrameId = useRef<number | null>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout>()
  const [hiddenPercent, setHiddenPercent] = useState(100)

  useLayoutEffect(() => {
    function calculateNotchPosition() {
      if (notchRef.current) {
        notchPositionRef.current = notchRef.current.getBoundingClientRect()
      }
    }

    window.addEventListener('resize', calculateNotchPosition)

    requestAnimationFrame(calculateNotchPosition)

    return () => window.removeEventListener('resize', calculateNotchPosition)
  }, [notchRef])

  const mmTarget: MousemoveTarget = mousemoveTarget ?? document

  useEffect(() => {
    const handleMouseMovement = (event: Pick<MouseEvent, 'clientX' | 'clientY'>) => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }

      hideTimeoutRef.current = setTimeout(() => {
        // if the mouse is not moved in 5 seconds, set the hidden percent to 100
        setHiddenPercent(100)
      }, 5000)

      // let the currently scheduled frame finish
      if (animationFrameId.current) return

      animationFrameId.current = requestAnimationFrame(() => {
        animationFrameId.current = null

        if (notchRef.current) {
          notchPositionRef.current = notchRef.current.getBoundingClientRect()
        }

        if (notchPositionRef.current) {
          const notchRect = notchPositionRef.current

          const distance = getDistanceFromMouseToNotch(
            notchRect,
            {
              x: event.clientX,
              y: event.clientY,
            },
            { clampAxis: clampEdge === 'top' || clampEdge === 'bottom' ? 'y' : 'x' },
          )

          let newHiddenPercent: number

          if (distance > PEEK_ZONE_DISTANCE_THRESHOLD) {
            newHiddenPercent = 100
          } else {
            const rawPercent = distance / PEEK_ZONE_DISTANCE_THRESHOLD
            // in peeking state, at most 50% of the notch is visible
            newHiddenPercent = rawPercent * 50 + 50
          }

          // round to nearest 2% to reduce re-renders
          const roundedPercent = Math.round(newHiddenPercent / 2) * 2

          // Only update state if the rounded value actually changed
          setHiddenPercent(prev => {
            if (prev === roundedPercent) {
              return prev
            }
            return roundedPercent
          })
        }
      })
    }

    mmTarget.addEventListener('mousemove', handleMouseMovement)
    return () => {
      mmTarget.removeEventListener('mousemove', handleMouseMovement)
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
        animationFrameId.current = null
      }

      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [mmTarget, notchRef, clampEdge])

  return { hiddenPercent }
}

/**
 * Get the distance from the mouse to the notch
 * @param notchRect The bounding client rect of the notch
 * @param mousePosition The position of the mouse
 * @param mousePosition.x The x coordinate of the mouse
 * @param mousePosition.y The y coordinate of the mouse
 * @param options specify a clampAxis of 'x' or 'y'
 * @returns The distance from the mouse to the notch
 */
export function getDistanceFromMouseToNotch(
  notchRect: DOMRect,
  mousePosition: { readonly x: number; readonly y: number },
  options: { clampAxis?: 'x' | 'y' } = { clampAxis: 'x' },
) {
  const dx = Math.min(
    Math.abs(mousePosition.x - notchRect.left),
    Math.abs(mousePosition.x - notchRect.right),
  )

  const dy = Math.min(
    Math.abs(mousePosition.y - notchRect.top),
    Math.abs(mousePosition.y - notchRect.bottom),
  )

  const hypotDist = Math.hypot(dx, dy)

  if (options.clampAxis === 'x' && dx < PEEK_ZONE_CLAMPED_EDGE_THRESHOLD)
    return Math.min(hypotDist, PEEK_ZONE_CLAMPED_EDGE_SIMULATED_DISTANCE)

  if (options.clampAxis === 'y' && dy < PEEK_ZONE_CLAMPED_EDGE_THRESHOLD)
    return Math.min(hypotDist, PEEK_ZONE_CLAMPED_EDGE_SIMULATED_DISTANCE)

  return hypotDist
}
